import {
  AlertTriangle,
  Award,
  Bell,
  Calendar,
  Camera,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  DollarSign,
  FileCheck,
  FileEdit,
  FileImage,
  FileQuestion,
  Files,
  FileText,
  FileWarning,
  FolderOpen,
  HardHat,
  LayoutDashboard,
  LucideIcon,
  Milestone,
  Package,
  Receipt,
  Settings,
  Shield,
  ShoppingCart,
  Truck,
  Users,
} from 'lucide-react';

export type NavigationScope = 'global' | 'project';

export interface NavigationItem {
  key: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  children?: NavigationItem[];
  route: (scope: NavigationScope, projectId?: string) => string;
  badge?: number;
  /** Whether this item requires a project or works at org level */
  level?: 'project' | 'org';
}

export interface NavigationGroup {
  key: string;
  label: string;
  items: NavigationItem[];
}

// Mega-menu specific groups for "All Items"
export const MEGA_MENU_GROUPS: NavigationGroup[] = [
  {
    key: 'financials',
    label: 'Financials',
    items: [
      {
        key: 'purchase-orders',
        label: 'PO & Subcontracts',
        icon: ShoppingCart,
        permission: 'purchase_orders.view',
        route: (scope, projectId) => `/projects/${projectId}/purchase-orders`,
      },
      {
        key: 'bills',
        label: 'Bills',
        icon: Receipt,
        permission: 'bills.view',
        route: (scope, projectId) => `/projects/${projectId}/bills`,
      },
      {
        key: 'change-orders',
        label: 'Change Orders',
        icon: ClipboardList,
        permission: 'change_orders.view',
        route: (scope, projectId) => `/projects/${projectId}/change-orders`,
      },
      {
        key: 'client-invoices',
        label: 'Client Invoices',
        icon: CreditCard,
        permission: 'client_invoices.view',
        route: (scope, projectId) => `/projects/${projectId}/client-invoices`,
      },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    items: [
      {
        key: 'daily-logs',
        label: 'Daily Logs',
        icon: FileEdit,
        permission: 'daily_logs.view',
        route: (scope, projectId) => `/projects/${projectId}/daily-logs`,
      },
      {
        key: 'timesheets',
        label: 'Timesheets',
        icon: Clock,
        permission: 'timesheets.view',
        route: (scope, projectId) => `/projects/${projectId}/timesheets`,
      },
      {
        key: 'todos',
        label: 'To-Dos',
        icon: CheckSquare,
        permission: 'todos.view',
        route: (scope, projectId) => `/projects/${projectId}/todos`,
      },
      {
        key: 'rfis',
        label: 'RFIs',
        icon: FileQuestion,
        permission: 'rfis.view',
        route: (scope, projectId) => `/projects/${projectId}/rfis`,
      },
      {
        key: 'files',
        label: 'Files',
        icon: Files,
        permission: 'files.view',
        route: (scope, projectId) => `/projects/${projectId}/files`,
      },
      {
        key: 'calendar',
        label: 'Calendar',
        icon: Calendar,
        permission: 'calendar.view',
        route: (scope, projectId) => `/projects/${projectId}/calendar`,
      },
    ],
  },
  {
    key: 'management',
    label: 'Management',
    items: [
      {
        key: 'vendors',
        label: 'Vendors',
        icon: Truck,
        level: 'org',
        route: () => '/vendors',
      },
    ],
  },
];

// Main navigation configuration (existing)
export const MENU_GROUPS: NavigationGroup[] = [
  {
    key: 'workspace',
    label: 'Workspace',
    items: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        route: (scope, projectId) =>
          scope === 'global' ? '/dashboard' : `/projects/${projectId}/dashboard`,
      },
      {
        key: 'projects',
        label: 'Projects',
        icon: FolderOpen,
        route: () => '/projects',
      },
      {
        key: 'notifications',
        label: 'Notifications',
        icon: Bell,
        route: () => '/notifications',
        badge: 0, // Will be populated dynamically
      },
      {
        key: 'settings',
        label: 'Settings',
        icon: Settings,
        route: () => '/settings',
      },
    ],
  },
  {
    key: 'planning-control',
    label: 'Planning & Control',
    items: [
      {
        key: 'tasks',
        label: 'Tasks',
        icon: CheckSquare,
        permission: 'tasks.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/tasks' : `/projects/${projectId}/tasks`,
      },
      {
        key: 'milestones',
        label: 'Milestones',
        icon: Milestone,
        permission: 'milestones.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/milestones' : `/projects/${projectId}/milestones`,
      },
      {
        key: 'meetings',
        label: 'Meetings & Minutes of Meeting',
        icon: Calendar,
        permission: 'meetings.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/meetings' : `/projects/${projectId}/meetings`,
      },
      {
        key: 'rfis',
        label: 'RFIs & Submittals',
        icon: FileQuestion,
        permission: 'rfis.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/rfis' : `/projects/${projectId}/rfis`,
      },
      {
        key: 'change-orders',
        label: 'Change Orders',
        icon: ClipboardList,
        permission: 'change_orders.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/change-orders' : `/projects/${projectId}/change-orders`,
      },
      {
        key: 'specs',
        label: 'Specifications',
        icon: FileText,
        permission: 'specs.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/specs' : `/projects/${projectId}/specs`,
      },
      {
        key: 'drawings',
        label: 'Drawings',
        icon: FileImage,
        permission: 'drawings.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/drawings' : `/projects/${projectId}/drawings`,
      },
      {
        key: 'punch',
        label: 'Punch List & Inspections',
        icon: ClipboardCheck,
        permission: 'punch.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/punch' : `/projects/${projectId}/punch`,
      },
    ],
  },
  {
    key: 'field-compliance',
    label: 'Field & Compliance',
    items: [
      {
        key: 'daily-logs',
        label: 'Daily Logs',
        icon: FileEdit,
        permission: 'daily_logs.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/daily-logs' : `/projects/${projectId}/daily-logs`,
      },
      {
        key: 'field-reports',
        label: 'Field Reports',
        icon: FileCheck,
        permission: 'field_reports.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/field-reports' : `/projects/${projectId}/field-reports`,
      },
      {
        key: 'safety',
        label: 'Safety Reports',
        icon: Shield,
        permission: 'safety.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/safety' : `/projects/${projectId}/safety`,
      },
      {
        key: 'quality',
        label: 'Quality Reports',
        icon: Award,
        permission: 'quality.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/quality' : `/projects/${projectId}/quality`,
      },
      {
        key: 'compliance',
        label: 'Compliance Tracking',
        icon: FileWarning,
        permission: 'compliance.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/compliance' : `/projects/${projectId}/compliance`,
      },
      {
        key: 'incidents',
        label: 'Incident Reporting',
        icon: AlertTriangle,
        permission: 'incidents.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/incidents' : `/projects/${projectId}/incidents`,
      },
    ],
  },
  {
    key: 'workforce-assets',
    label: 'Workforce & Assets',
    items: [
      {
        key: 'teams',
        label: 'Teams (Internal & Contractors)',
        icon: Users,
        permission: 'teams.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/teams' : `/projects/${projectId}/teams`,
        children: [
          {
            key: 'teams-internal',
            label: 'Internal Teams',
            icon: Users,
            permission: 'teams.view',
            route: (scope, projectId) =>
              scope === 'global'
                ? '/global/teams/internal'
                : `/projects/${projectId}/teams/internal`,
          },
          {
            key: 'teams-contractors',
            label: 'Contractors',
            icon: Users,
            permission: 'teams.view',
            route: (scope, projectId) =>
              scope === 'global'
                ? '/global/teams/contractors'
                : `/projects/${projectId}/teams/contractors`,
          },
        ],
      },
      {
        key: 'manpower',
        label: 'Manpower',
        icon: HardHat,
        permission: 'manpower.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/manpower' : `/projects/${projectId}/manpower`,
      },
      {
        key: 'equipment',
        label: 'Equipment Tracking',
        icon: Truck,
        permission: 'equipment.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/equipment' : `/projects/${projectId}/equipment`,
      },
    ],
  },
  {
    key: 'site-logistics',
    label: 'Site Logistics & Docs',
    items: [
      {
        key: 'deliveries',
        label: 'Deliveries',
        icon: Package,
        permission: 'deliveries.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/deliveries' : `/projects/${projectId}/deliveries`,
      },
      {
        key: 'photos',
        label: 'Photos',
        icon: Camera,
        permission: 'photos.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/photos' : `/projects/${projectId}/photos`,
      },
      {
        key: 'documents',
        label: 'Documents (Central)',
        icon: Files,
        permission: 'documents.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/documents' : `/projects/${projectId}/documents`,
      },
    ],
  },
  {
    key: 'financials',
    label: 'Financials',
    items: [
      {
        key: 'budgets',
        label: 'Budgets',
        icon: DollarSign,
        permission: 'budgets.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/budgets' : `/projects/${projectId}/budgets`,
      },
      {
        key: 'invoicing',
        label: 'Invoicing & Payments',
        icon: Receipt,
        permission: 'invoicing.view',
        route: (scope, projectId) =>
          scope === 'global' ? '/global/invoicing' : `/projects/${projectId}/invoicing`,
      },
    ],
  },
];

// Helper to get flattened list of all navigation items
export function getAllNavigationItems(): NavigationItem[] {
  const items: NavigationItem[] = [];

  MENU_GROUPS.forEach(group => {
    group.items.forEach(item => {
      items.push(item);
      if (item.children) {
        items.push(...item.children);
      }
    });
  });

  return items;
}

// Helper to find item by key
export function findNavigationItem(key: string): NavigationItem | undefined {
  return getAllNavigationItems().find(item => item.key === key);
}

// Helper to check if user has permission for an item
export function hasPermission(item: NavigationItem, userPermissions?: string[]): boolean {
  if (!item.permission) return true;
  if (!userPermissions) return false;
  return userPermissions.includes(item.permission);
}

// Legacy route redirects mapping
export const LEGACY_REDIRECTS: Record<string, (params?: any) => string> = {
  // Global redirects from /dashboard/* to /global/*
  '/dashboard/tasks': () => '/global/tasks',
  '/dashboard/meetings': () => '/global/meetings',
  '/dashboard/drawings': () => '/global/drawings',
  '/dashboard/photos': () => '/global/photos',
  '/dashboard/manpower': () => '/global/manpower',
  '/dashboard/contracts': () => '/global/contracts',
  '/dashboard/reports': () => '/global/reports',

  // Project-specific redirects (dynamic)
  '/projects/:projectId/dashboard/tasks': params => `/projects/${params.projectId}/tasks`,
  '/projects/:projectId/dashboard/meetings': params => `/projects/${params.projectId}/meetings`,
  '/projects/:projectId/dashboard/drawings': params => `/projects/${params.projectId}/drawings`,
  '/projects/:projectId/dashboard/photos': params => `/projects/${params.projectId}/photos`,
  '/projects/:projectId/dashboard/manpower': params => `/projects/${params.projectId}/manpower`,
};

// Storage keys for persistence
export const STORAGE_KEYS = {
  MENU_MODE: 'builderbell_menu_mode',
  GROUP_STATES: 'builderbell_menu_groups',
  LAST_ROUTE: 'builderbell_last_route',
  SELECTED_PROJECT: 'builderbell_selected_project',
} as const;
